/** Renders and coordinates the Image Audit queue's approve-all action. */
import * as React from "react";
import {useState} from "react";
import {Button} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {Dialogs} from "../../../DialogProvider";
import {approveAuditImages, ImageAuditApprovalResult} from "./imageAuditApproval";

type ImageAuditBulkApproveProps = {
   imageIds: string[]
   approve: (imageId: string) => Promise<unknown>
   onComplete: (result: ImageAuditApprovalResult) => Promise<void> | void
   onBusyChange?: (busy: boolean) => void
   disabled?: boolean
}

/** Lets an administrator confirm and approve every image currently in the audit queue. */
export const ImageAuditBulkApprove = ({
   imageIds,
   approve,
   onComplete,
   onBusyChange,
   disabled = false
}: ImageAuditBulkApproveProps) => {
   const [completed, setCompleted] = useState<number | null>(null);
   const busy = completed !== null;

   /** Runs the complete queue while keeping per-image failures isolated. */
   const runApproval = async () => {
      setCompleted(0);
      onBusyChange?.(true);
      try {
         const result = await approveAuditImages(imageIds, approve, setCompleted);
         await onComplete(result);

         if (result.failedCount > 0) {
            Dialogs.info({
               title: "Some images could not be approved",
               subtitle: `${result.approvedCount} approved, ${result.failedCount} failed. The failed images remain in the audit queue.`
            });
         }
      } finally {
         setCompleted(null);
         onBusyChange?.(false);
      }
   }

   /** Confirms the exact queue size before starting the bulk action. */
   const confirmApproval = () => {
      Dialogs.confirm({
         title: `Approve all ${imageIds.length} pending images?`,
         subtitle: "This approves every pending image in the audit queue, regardless of the current search.",
         defaultNo: true,
         buttonYes: "Approve all",
         buttonNo: "Cancel"
      }, () => void runApproval());
   }

   return <Button
      variant={"contained"}
      color={"success"}
      startIcon={<CheckCircleIcon/>}
      disabled={disabled || busy || imageIds.length === 0}
      onClick={confirmApproval}
   >
      {busy ? `Approving ${completed} / ${imageIds.length}…` : "Approve all"}
   </Button>;
}
